import {Component, Input, OnInit, Optional} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ValueService} from '@ngmedax/value';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NgbTooltip} from '@ng-bootstrap/ng-bootstrap';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {QuestionnaireVariablesService} from '../../services/questionnaire-variables.service';
import {FormulaValidationService, FormulaValidationError} from '../../services/formula-validation.service';
import {ToastService} from '../../../../../toast';


// hack to inject decorator declarations. must occur before class declaration!
export interface FormulaVariablesComponent extends Translatable {}

@Component({
  selector: 'app-qa-formula-variables',
  templateUrl: './formula-variables.component.html',
  styleUrls: ['./formula-variables.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class FormulaVariablesComponent implements OnInit {
  @Input() questionnaire: Questionnaire;
  public form: FormGroup;

  /**
   * Currently visible formula error tooltip (positioned at the offending token)
   */
  public tooltip: {varName: string, message: string, top: number, left: number} = null;

  /**
   * Current validation state per formula variable, refreshed by the form validators
   */
  private expressionErrors: {[varName: string]: {message: string, error: FormulaValidationError}} = {};

  /**
   * Cache for the autofix candidates (autofix compiles, so don't run it on every change detection)
   */
  private fixableCache: {key: string, names: string[]} = {key: null, names: []};

  private varPath  = ['meta', 'options', 'variables'];
  private varNamePattern = /^[a-zA-Z_][a-zA-Z0-9_]{2,49}$/;
  private errors = {
    'pattern': this._(KEYS.EDITOR.INVALID_VAR_NAME_TEXT),
    'invalidExpression': this._(KEYS.EDITOR.INVALID_EXPRESSION),
    'invalidCircularExpression': this._(KEYS.EDITOR.INVALID_CIRCULAR_EXPRESSION)
  }

  private formulaErrors: {[type: string]: string} = {
    'syntax': this._(KEYS.EDITOR.FORMULA_SYNTAX_ERROR),
    'unknownVariable': this._(KEYS.EDITOR.FORMULA_UNKNOWN_VARIABLE),
    'unknownMathFunction': this._(KEYS.EDITOR.FORMULA_UNKNOWN_MATH_FUNCTION),
    'invalidFunctionCall': this._(KEYS.EDITOR.INVALID_EXPRESSION),
    'quotes': this._(KEYS.EDITOR.INVALID_EXPRESSION),
    'circular': this._(KEYS.EDITOR.INVALID_CIRCULAR_EXPRESSION)
  }

  private formulaErrorDetails: {[code: string]: string} = {
    'missingClosingParen': this._(KEYS.EDITOR.FORMULA_MISSING_CLOSING_PAREN),
    'extraClosingParen': this._(KEYS.EDITOR.FORMULA_EXTRA_CLOSING_PAREN)
  }

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private questionnaireVariables: QuestionnaireVariablesService,
    private formulaValidation: FormulaValidationService,
    private formBuilder: FormBuilder,
    private toast: ToastService,
    private value: ValueService
  ) {
  }

  /**
   * Initializes formula variables and updates form
   */
  public ngOnInit() {
    this.initFormulaVariables();
    this.updateForm();
  }

  /**
   * Adds new formula variable
   */
  public onAdd() {
    const varPrefix = 'formula';
    const formulaVariables = this.questionnaireVariables.getVariablesForScope('formula');

    for (let i = 1; i < 1000; i++) {
      const varName = `${varPrefix}${i}`;
      if (!formulaVariables[varName]) {
        formulaVariables[varName] = {expression: ''};
        break;
      }
    }

    this.updateForm();
  }

  /**
   * Event handler for when a formula var is changed
   *
   * @param {string} varName
   * @param {string} previousVarName
   */
  public onEdit(varName: string, previousVarName: string) {
    const expressionValue = this.form.get(previousVarName + 'Expression')?.value;
    this.renameVariable('formula', varName, expressionValue, previousVarName);
    this.updateForm();
  }

  /**
   * Event handler for when formula var is reset to previous value
   *
   * @param {string} previousVarName
   */
  public onUndo(previousVarName: string) {
    const variables = this.questionnaireVariables.getVariablesForScope('formula');
    const previousExpression = this.value.get(variables, [previousVarName, 'expression']) || '';

    this.form.get(previousVarName).reset(previousVarName);
    this.form.get(previousVarName + 'Expression').reset(previousExpression);
  }

  /**
   * Event handler for when formula var is removed
   *
   * @param {string} varName
   */
  public onRemove(varName: string) {
    const formulaVariables = this.questionnaireVariables.getVariablesForScope('formula');
    formulaVariables[varName] && delete(formulaVariables[varName]);
    delete(this.expressionErrors[varName]);
    this.updateForm();
  }

  /**
   * Fixes given formula when it is mechanically fixable (unbalanced parentheses,
   * "else (x)"). Writes the fixed expression directly into the questionnaire
   * model, so the per formula save step is not needed. Persisting still happens
   * with the regular questionnaire save
   *
   * @param {string} varName
   */
  public onAutoFix(varName: string) {
    const variables = this.questionnaireVariables.getVariablesForScope('formula');
    const control = this.form.get(varName + 'Expression');

    if (!control || !variables[varName]) {
      return;
    }

    const fixed = this.formulaValidation.autofix(control.value + '', this.questionnaire);

    if (fixed === null) {
      return;
    }

    let stored = fixed.trim();
    // same save transformation as renameVariable
    !stored.match(/^\([\s\S]*\)$/) && (stored = `(${stored})`);
    variables[varName].expression = stored;

    control.setValue(fixed);
    control.markAsPristine();

    (this.tooltip && this.tooltip.varName === varName) && (this.tooltip = null);
    this.toast.success(`${this._(KEYS.EDITOR.FORMULA_AUTOFIX_DONE)}: ${varName}`);
  }

  /**
   * Returns true when the formula of given variable is broken and the autofix
   * can repair it. Used to enable the per formula fix button
   *
   * @param {string} varName
   * @returns {boolean}
   */
  public isFormulaFixable(varName: string): boolean {
    return this.getFixableFormulaNames().indexOf(varName) !== -1;
  }

  /**
   * Returns names of broken formulas that the autofix can repair. Cached by the
   * current expression values, as this runs from the template
   *
   * @returns {string[]}
   */
  public getFixableFormulaNames(): string[] {
    if (!this.form) {
      return [];
    }

    const varNames = this.getFormulaVariableNames(false);
    const key = varNames.map(name => {
      const control = this.form.get(name + 'Expression');
      return `${name}|${control ? control.value : ''}`;
    }).join('##');

    if (this.fixableCache.key === key) {
      return this.fixableCache.names;
    }

    const names = varNames.filter(name => {
      const control = this.form.get(name + 'Expression');

      if (!control || !control.errors) {
        return false;
      }

      return this.formulaValidation.autofix(control.value + '', this.questionnaire) !== null;
    });

    this.fixableCache = {key, names};
    return names;
  }

  /**
   * Returns current error message for the error box below given formula
   *
   * @param {string} varName
   * @returns {string}
   */
  public getFormulaErrorText(varName: string): string {
    const info = this.expressionErrors[varName];
    return info ? info.message : '';
  }

  /**
   * Returns true when the formula of given variable is currently invalid
   *
   * @param {string} varName
   * @returns {boolean}
   */
  public hasFormulaError(varName: string): boolean {
    return !!this.expressionErrors[varName];
  }

  /**
   * Splits the current expression text into segments for the highlight backdrop.
   * The segment covering the offending token is marked as error
   *
   * @param {string} varName
   * @returns {{text: string, isError: boolean}[]}
   */
  public getExpressionSegments(varName: string): {text: string, isError: boolean}[] {
    const control = this.form && this.form.get(varName + 'Expression');
    const text = (control && control.value !== null && control.value !== undefined) ? control.value + '' : '';
    const info = this.expressionErrors[varName];
    const range = info && info.error && info.error.range;

    if (!range || range.start >= text.length) {
      return [{text: text, isError: false}];
    }

    const end = Math.min(range.end, text.length);

    return [
      {text: text.slice(0, range.start), isError: false},
      {text: text.slice(range.start, end), isError: true},
      {text: text.slice(end), isError: false}
    ];
  }

  /**
   * Shows the formula error tooltip for given variable at the offending token.
   * Used on mouseover and while typing. Hides the tooltip when the formula is valid
   *
   * @param {string} varName
   * @param {HTMLElement} wrapEl
   */
  public showFormulaTip(varName: string, wrapEl: HTMLElement) {
    // wait a tick so the highlight backdrop reflects the latest input
    setTimeout(() => {
      const info = this.expressionErrors[varName];

      if (!info) {
        (this.tooltip && this.tooltip.varName === varName) && (this.tooltip = null);
        return;
      }

      let top = 34;
      let left = 8;

      const span = <HTMLElement>wrapEl.querySelector('.formula-error-token');
      const backdrop = <HTMLElement>wrapEl.querySelector('.formula-highlight-backdrop');

      if (span && backdrop) {
        top = span.offsetTop - backdrop.scrollTop + span.offsetHeight + 6;
        left = Math.max(0, span.offsetLeft - backdrop.scrollLeft);
      }

      this.tooltip = {varName, message: info.message, top, left};
    });
  }

  /**
   * Hides the formula error tooltip unless the formula field is still focused
   *
   * @param {string} varName
   * @param {HTMLElement} textareaEl
   */
  public hideFormulaTip(varName: string, textareaEl: HTMLElement) {
    if (document.activeElement === textareaEl) {
      return;
    }

    (this.tooltip && this.tooltip.varName === varName) && (this.tooltip = null);
  }

  /**
   * Hides the formula error tooltip for given variable unconditionally
   *
   * @param {string} varName
   */
  public closeFormulaTip(varName: string) {
    (this.tooltip && this.tooltip.varName === varName) && (this.tooltip = null);
  }

  /**
   * Keeps the highlight backdrop in sync when the textarea scrolls and
   * repositions a visible tooltip
   *
   * @param {HTMLElement} backdropEl
   * @param {EventTarget} textareaEl
   * @param {string} varName
   * @param {HTMLElement} wrapEl
   */
  public onExpressionScroll(backdropEl: HTMLElement, textareaEl: EventTarget, varName: string, wrapEl: HTMLElement) {
    const textarea = <HTMLElement>textareaEl;
    backdropEl.scrollTop = textarea.scrollTop;
    backdropEl.scrollLeft = textarea.scrollLeft;
    this.tooltip && this.tooltip.varName === varName && this.showFormulaTip(varName, wrapEl);
  }

  /**
   * Handles keyboard shortcuts on formula variable name input fields.
   * Enter saves the variable, Ctrl+S saves and stops event propagation.
   *
   * @param {KeyboardEvent} event
   * @param {HTMLInputElement} formulaInputEl
   * @param {string} formulaVarName
   */
  public onNameKeyDown(event: KeyboardEvent, formulaInputEl: HTMLInputElement, formulaVarName: string) {
    const isCtrlS = (event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S');
    const isEnter = event.key === 'Enter';

    if (!isCtrlS && !isEnter) {
      return;
    }

    event.preventDefault();

    if (isCtrlS) {
      event.stopPropagation();
    }

    const hasErrors = this.form.get(formulaVarName).errors
      || this.form.get(formulaVarName + 'Expression').errors;

    if (hasErrors || !this.form.dirty) {
      return;
    }

    this.onEdit(formulaInputEl.value, formulaVarName);
  }

  /**
   * Handles keyboard shortcuts on formula expression textarea.
   * Ctrl+Enter saves the variable, Ctrl+S saves and stops event propagation.
   *
   * @param {KeyboardEvent} event
   * @param {HTMLInputElement} formulaInputEl
   * @param {string} formulaVarName
   */
  public onExpressionKeyDown(
    event: KeyboardEvent, formulaInputEl: HTMLInputElement, formulaVarName: string
  ) {
    const isCtrlS = (event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S');
    const isCtrlEnter = (event.ctrlKey || event.metaKey) && event.key === 'Enter';

    if (!isCtrlS && !isCtrlEnter) {
      return;
    }

    event.preventDefault();

    if (isCtrlS) {
      event.stopPropagation();
    }

    const hasErrors = this.form.get(formulaVarName).errors
      || this.form.get(formulaVarName + 'Expression').errors;

    if (hasErrors || !this.form.dirty) {
      return;
    }

    this.onEdit(formulaInputEl.value, formulaVarName);
  }

  /**
   * Shows tooltip when current form element value is invalid (name input fields)
   *
   * @param {NgbTooltip} tooltip
   * @param {AbstractControl} formEl
   */
  public onCheckForTooltip(tooltip: NgbTooltip, formEl: AbstractControl) {
    if (formEl.dirty && formEl.errors) {
      formEl.errors.pattern && tooltip.open({tooltipMsg: this.errors.pattern});
      return;
    }

    tooltip && tooltip.isOpen() && tooltip.close();
  }

  /**
   * Builds a user readable error message for given formula validation error
   *
   * @param {FormulaValidationError} error
   * @returns {string}
   */
  private getFormulaErrorMessage(error: FormulaValidationError): string {
    const message = this.formulaErrors[error.type] || this.errors.invalidExpression;

    if (error.detailCode && this.formulaErrorDetails[error.detailCode]) {
      return `${message}: ${this.formulaErrorDetails[error.detailCode]}`;
    }

    const withoutDetail = error.type === 'quotes' || error.type === 'circular';
    return (!withoutDetail && error.detail) ? `${message}: ${error.detail}` : message;
  }

  /**
   * Returns array of formula variables by questionnaire or by form
   *
   * @param {boolean} byForm
   * @returns string[]
   */
  public getFormulaVariableNames(byForm = false): string[] {
    const formControls = this.value.get(this.form, ['controls']) || {};
    return Object.keys(byForm ? formControls : this.questionnaireVariables.getVariablesForScope('formula'));
  }

  /**
   * Returns variable data
   *
   * @param {string} varName
   * @returns {{type: string}}
   */
  public getVariableData(varName: string): {format: string, expression: string | number} {
    return <{format: string, expression: string}>
      this.questionnaireVariables.getVariablesForScope('formula')[varName] || {format: 'text', expression: ''};
  }

  /**
   * Renames variable
   */
  public renameVariable(scope: string, varName: string, expressionValue: string, previousVarName: string) {
    const variables = this.questionnaireVariables.getVariablesForScope(scope);

    if (!variables[previousVarName]) {
      return;
    }

    variables[varName] = this.value.clone(variables[previousVarName]);
    expressionValue = expressionValue.trim();
    // [\s\S] instead of "." so multiline formulas don't get wrapped again on every save
    !expressionValue.match(/^\([\s\S]*\)$/) && (expressionValue = `(${expressionValue})`);
    expressionValue !== '' && (variables[varName].expression = expressionValue);
    previousVarName !== varName && delete(variables[previousVarName]);
  }

  /**
   * Updates formula variables form by questionnaire formula variables
   */
  private updateForm() {
    const varNames = this.getFormulaVariableNames();
    const dynFormGroup = {};

    this.expressionErrors = {};

    for (const varName of varNames) {
      dynFormGroup[varName] = this.formBuilder.control(varName, [Validators.pattern(this.varNamePattern)]);

      const expressionValue = this.getVariableData(varName).expression || '';
      dynFormGroup[varName + 'Expression'] = this.formBuilder.control(expressionValue, (control: AbstractControl) => {
        const error = this.formulaValidation.validate(control.value + '', this.questionnaire);

        if (!error) {
          delete(this.expressionErrors[varName]);
          return null;
        }

        this.expressionErrors[varName] = {message: this.getFormulaErrorMessage(error), error};

        const errorKey = error.type === 'circular' ? 'invalidCircularExpression' : 'invalidExpression';
        return {[errorKey]: true, formulaError: this.getFormulaErrorMessage(error)};
      });
    }

    this.form = this.formBuilder.group(dynFormGroup);
  }

  /**
   * Initializes questionnaire formula variables in questionnaire
   */
  private initFormulaVariables() {
    let current = this.questionnaire;
    [...this.varPath, 'formula'].forEach((path) => {
      !current[path] && (current[path] = {});
      current = current[path];
    });
  }
}
